import { useEffect, useRef, useState } from "react";

interface PageTransitionProps {
  /** The current page key — changing this triggers the transition */
  pageKey: string;
  children: React.ReactNode;
}

type Phase = "enter" | "visible";

/**
 * Wraps page content and plays a subtle fade + upward-slide animation
 * whenever `pageKey` changes.  Uses only `opacity` and `transform` so the
 * browser can composite the animation on the GPU without triggering layout.
 *
 * Design choices:
 * - No external library — uses the `fadeSlideUp` / `scaleIn` keyframes
 *   already defined in index.css.
 * - 220 ms duration keeps the transition snappy on mobile.
 * - `will-change: transform, opacity` is applied only during the animation
 *   frame and removed afterwards to avoid unnecessary GPU layer promotion.
 * - iOS-safe: no `overflow: hidden` on the wrapper, no scroll-position reset.
 */
export function PageTransition({ pageKey, children }: PageTransitionProps) {
  const [phase, setPhase] = useState<Phase>("visible");
  const [displayedKey, setDisplayedKey] = useState(pageKey);
  const [displayedChildren, setDisplayedChildren] = useState(children);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (pageKey === displayedKey) {
      // Same page — update children in place without re-animating
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisplayedChildren(children);
      return;
    }

    // Cancel any in-flight animation
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

    // Swap content and trigger enter animation on the next paint
    setDisplayedKey(pageKey);
    setDisplayedChildren(children);
    setPhase("enter");

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        setPhase("visible");
      });
    });

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  // Keep displayed children in sync when same page re-renders
  useEffect(() => {
    if (pageKey === displayedKey) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisplayedChildren(children);
    }
  }, [children, pageKey, displayedKey]);

  const isEntering = phase === "enter";

  return (
    <div
      className="flex flex-col flex-1 min-h-0 h-full"
      style={{
        opacity: isEntering ? 0 : 1,
        transform: isEntering ? "translateY(10px) scale(0.995)" : "translateY(0) scale(1)",
        transition: isEntering
          ? "none"
          : "opacity 220ms ease-out, transform 220ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        willChange: isEntering ? "transform, opacity" : "auto",
      }}
    >
      {displayedChildren}
    </div>
  );
}
